import { useState, useEffect } from 'react'
import { usePortalClient } from '../hooks/usePortalClient'
import { supabase } from '../../../shared/lib/supabase'

function PortalM365Report() {
  const { clientId, loading: clientLoading } = usePortalClient()
  const [blobUrl, setBlobUrl] = useState(null)
  const [reportName, setReportName] = useState(null)
  const [reportDate, setReportDate] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!clientId) return

    async function fetchReport() {
      try {
        const { data, error: fetchError } = await supabase
          .from('maester_reports')
          .select('*')
          .eq('client_id', clientId)
          .order('created_at', { ascending: false })
          .limit(1)
          .single()

        if (fetchError) {
          if (fetchError.code === 'PGRST116') {
            setError('no-report')
            return
          }
          throw fetchError
        }

        setReportName(data.file_name)
        setReportDate(data.created_at)

        // Generate signed URL and create blob
        const { data: urlData, error: urlError } = await supabase.storage
          .from('maester-reports')
          .createSignedUrl(data.file_path, 3600)

        if (urlError) throw urlError

        const htmlRes = await fetch(urlData.signedUrl)
        if (htmlRes.ok) {
          const html = await htmlRes.text()
          const blob = new Blob([html], { type: 'text/html' })
          setBlobUrl(URL.createObjectURL(blob))
        }
      } catch (err) {
        console.error('[PortalM365Report] Error:', err)
        setError(err.message || 'Failed to load report')
      } finally {
        setLoading(false)
      }
    }

    fetchReport()

    return () => {
      setBlobUrl(prev => {
        if (prev) URL.revokeObjectURL(prev)
        return null
      })
    }
  }, [clientId])

  if (loading || clientLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-400" />
      </div>
    )
  }

  // No report
  if (error === 'no-report') {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg p-8 text-center border border-gray-200 dark:border-gray-700">
        <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center bg-gray-50 dark:bg-gray-800/50">
          <svg className="w-8 h-8 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
          </svg>
        </div>
        <h2 className="text-lg font-semibold mb-2 text-gray-900 dark:text-white">No M365 Report Available</h2>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Your advisor will upload your Microsoft 365 security scan results once the assessment is complete.
        </p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
        <p className="text-red-600">{error}</p>
      </div>
    )
  }

  return (
    <div className="space-y-4 flex flex-col min-h-[calc(100vh-12rem)]">
      {/* Header card */}
      <div className="bg-white dark:bg-gray-800 rounded-lg p-5 flex items-center justify-between border border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-gradient-to-br from-[#012438] to-[#213C4E]">
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
          </div>
          <div>
            <h2 className="text-sm font-semibold text-gray-900 dark:text-white">{reportName}</h2>
            <p className="text-xs mt-0.5 text-gray-400 dark:text-gray-500">
              Uploaded {new Date(reportDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
            </p>
          </div>
        </div>

        {blobUrl && (
          <a
            href={blobUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white transition-colors bg-[#012438] hover:bg-[#213C4E]"
          >
            Open in New Tab
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
        )}
      </div>

      {/* Iframe */}
      <div className="flex-1 bg-white dark:bg-gray-800 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 min-h-[600px]">
        {blobUrl && (
          <iframe
            src={blobUrl}
            sandbox="allow-scripts allow-same-origin"
            className="w-full h-full border-0 min-h-[600px]"
            title="M365 Security Report"
          />
        )}
      </div>
    </div>
  )
}

export default PortalM365Report
